import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Loader2, CheckCircle2, XCircle, Clock, BookOpen, User, BellRing,
  Target, Users, BarChart3, Database, Brain, TrendingUp, Monitor,
  Server, Cloud, HeartHandshake, DollarSign, MessageCircle,
  type LucideIcon,
} from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const TRACK_MAP: Record<string, { titulo: string; icon: LucideIcon }> = {
  produto: { titulo: "Gestão de Produto", icon: Target },
  "ux-design": { titulo: "UX e Design de Produto", icon: Users },
  "dados-bi": { titulo: "Análise de Dados, SQL e BI", icon: BarChart3 },
  "eng-dados": { titulo: "Engenharia de Dados", icon: Database },
  "ciencia-dados": { titulo: "Ciência de Dados", icon: Brain },
  marketing: { titulo: "Marketing Digital, Growth e Performance", icon: TrendingUp },
  frontend: { titulo: "Desenvolvimento Front-end", icon: Monitor },
  backend: { titulo: "Desenvolvimento Back-end", icon: Server },
  devops: { titulo: "Infraestrutura e DevOps", icon: Cloud },
  "cs-cx": { titulo: "Customer Success e CX", icon: HeartHandshake },
  revops: { titulo: "Operações de Vendas e Receita", icon: DollarSign },
  comunidades: { titulo: "Gestão de Comunidades e Conteúdo", icon: MessageCircle },
};

const STATUS_CONFIG: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  applied: { label: "Pendente", variant: "secondary" },
  accepted: { label: "Aprovada", variant: "default" },
  rejected: { label: "Recusada", variant: "destructive" },
  waitlist: { label: "Lista de espera", variant: "outline" },
  completed: { label: "Concluída", variant: "default" },
  cancelled: { label: "Cancelada", variant: "outline" },
};

export default function AdminMatriculas() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const { data: applications, isLoading } = useQuery({
    queryKey: ["admin_course_applications"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("course_applications")
        .select("*, courses(title, cycle, code), profiles:user_id(id, full_name, city, state)")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as any[];
    },
  });

  const { data: trackCounts } = useQuery({
    queryKey: ["admin_track_interest_counts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("track_interests")
        .select("track_id");
      if (error) throw error;
      const counts: Record<string, number> = {};
      (data as any[]).forEach((r) => {
        counts[r.track_id] = (counts[r.track_id] || 0) + 1;
      });
      return Object.entries(counts)
        .map(([track_id, count]) => ({ track_id, count }))
        .sort((a, b) => b.count - a.count);
    },
  });

  const updateStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase
        .from("course_applications")
        .update({ status })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin_course_applications"] });
      toast({ title: "Status atualizado" });
    },
    onError: (err: any) => {
      toast({ title: "Erro ao atualizar", description: err.message, variant: "destructive" });
    },
  });

  const filtered = applications?.filter(a => statusFilter === "all" || a.status === statusFilter) ?? [];

  const counts = {
    all: applications?.length ?? 0,
    applied: applications?.filter(a => a.status === "applied").length ?? 0,
    approved: applications?.filter(a => a.status === "accepted").length ?? 0,
    rejected: applications?.filter(a => a.status === "rejected").length ?? 0,
  };

  if (isLoading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-6xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">Matrículas</h1>
        <p className="text-muted-foreground text-sm mt-1">Gestão de aplicações para cursos</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: "Total", value: counts.all, icon: BookOpen, color: "text-primary" },
          { label: "Pendentes", value: counts.applied, icon: Clock, color: "text-accent" },
          { label: "Aprovadas", value: counts.approved, icon: CheckCircle2, color: "text-success" },
          { label: "Recusadas", value: counts.rejected, icon: XCircle, color: "text-destructive" },
        ].map(s => (
          <div key={s.label} className="bg-card rounded-xl border border-border p-4">
            <div className="flex items-center gap-2 mb-1">
              <s.icon className={`w-4 h-4 ${s.color}`} />
              <span className="text-sm text-muted-foreground">{s.label}</span>
            </div>
            <p className="text-2xl font-bold text-foreground">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3 mb-4">
        <span className="text-sm text-muted-foreground">Filtrar:</span>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="applied">Pendentes</SelectItem>
            <SelectItem value="accepted">Aprovadas</SelectItem>
            <SelectItem value="rejected">Recusadas</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="bg-card rounded-xl border border-border p-12 text-center">
          <BookOpen className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">Nenhuma matrícula encontrada</p>
        </div>
      ) : (
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="text-left p-4 font-medium text-muted-foreground">Aluno</th>
                  <th className="text-left p-4 font-medium text-muted-foreground">Curso</th>
                  <th className="text-left p-4 font-medium text-muted-foreground">Data</th>
                  <th className="text-left p-4 font-medium text-muted-foreground">Status</th>
                  <th className="text-right p-4 font-medium text-muted-foreground">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((app) => {
                  const profile = app.profiles;
                  const course = app.courses;
                  const config = STATUS_CONFIG[app.status] ?? STATUS_CONFIG.applied;
                  return (
                    <tr key={app.id} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <User className="w-4 h-4 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium text-foreground">{profile?.full_name || "Sem nome"}</p>
                            <p className="text-xs text-muted-foreground">
                              {[profile?.city, profile?.state].filter(Boolean).join(", ") || "—"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <p className="text-foreground">{course?.title || "—"}</p>
                        <p className="text-xs text-muted-foreground capitalize">{course?.cycle || ""}</p>
                      </td>
                      <td className="p-4 text-muted-foreground">
                        {format(new Date(app.created_at), "dd/MM/yyyy")}
                      </td>
                      <td className="p-4">
                        <Badge variant={config.variant}>{config.label}</Badge>
                      </td>
                      <td className="p-4 text-right">
                        {app.status === "applied" && (
                          <div className="flex items-center gap-2 justify-end">
                            <Button
                              size="sm"
                              variant="default"
                              onClick={() => updateStatus.mutate({ id: app.id, status: "accepted" })}
                              disabled={updateStatus.isPending}
                            >
                              <CheckCircle2 className="w-4 h-4 mr-1" />
                              Aprovar
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => updateStatus.mutate({ id: app.id, status: "rejected" })}
                              disabled={updateStatus.isPending}
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Recusar
                            </Button>
                          </div>
                        )}
                        {app.status !== "applied" && (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Track Interests */}
      <div className="mt-10">
        <div className="mb-4">
          <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
            <BellRing className="w-5 h-5 text-primary" />
            Interesses por Trilha Especialista
          </h2>
          <p className="text-sm text-muted-foreground mt-1">Quantidade de alunos que marcaram interesse em cada trilha</p>
        </div>

        {(!trackCounts || trackCounts.length === 0) ? (
          <div className="bg-card rounded-xl border border-border p-8 text-center">
            <BellRing className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-muted-foreground text-sm">Nenhum interesse registrado ainda</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {trackCounts.map((item) => {
              const info = TRACK_MAP[item.track_id];
              const Icon = info?.icon ?? BookOpen;
              return (
                <div key={item.track_id} className="bg-card rounded-xl border border-border p-4 flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{info?.titulo ?? item.track_id}</p>
                    <p className="text-xl font-bold text-foreground">{item.count}</p>
                    <p className="text-xs text-muted-foreground">aluno{item.count !== 1 ? "s" : ""}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
